import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { Switch } from '@/_shared/components/ui/switch';

import { SLACK_GUEST_ACCESS } from '../../lib/settingsSwitches';
import { SLACK_COMMANDS } from './types';
import { workspaceInitials } from './utils';

interface SlackConnectionCardProps {
  teamName: string | null;
  teamUrl: string | null;
  connectedUserCount: number;
  connectedChannelCount: number;
  isAdmin: boolean;
  settingsLoaded: boolean;
  guestAccessEnabled: boolean;
  guestAccessDisabled: boolean;
  onGuestAccessChange: (checked: boolean) => void;
  uninstallPending: boolean;
  onUninstall: () => void;
}

export function SlackConnectionCard({
  teamName,
  teamUrl,
  connectedUserCount,
  connectedChannelCount,
  isAdmin,
  settingsLoaded,
  guestAccessEnabled,
  guestAccessDisabled,
  onGuestAccessChange,
  uninstallPending,
  onUninstall,
}: SlackConnectionCardProps): React.ReactElement {
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between gap-3 px-4 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-sm font-semibold text-muted-foreground">
            {workspaceInitials(teamName)}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {teamUrl ? (
                <a
                  href={teamUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate text-sm font-semibold text-foreground hover:text-primary hover:underline"
                >
                  {teamName ?? 'Slack workspace'}
                </a>
              ) : (
                <span className="truncate text-sm font-semibold text-foreground">{teamName ?? 'Slack workspace'}</span>
              )}
              <Badge variant="success">Connected</Badge>
            </div>
            <p className="mt-1 truncate text-xs text-muted-foreground">
              {connectedUserCount} connected {connectedUserCount === 1 ? 'user' : 'users'} · {connectedChannelCount}{' '}
              connected {connectedChannelCount === 1 ? 'channel' : 'channels'}
            </p>
          </div>
        </div>
        {isAdmin && (
          <Button
            type="button"
            variant="secondary-destructive"
            className="shrink-0"
            loading={uninstallPending}
            onClick={onUninstall}
            label={uninstallPending ? 'Uninstalling…' : 'Uninstall'}
          />
        )}
      </div>

      {settingsLoaded && (
        <div className="flex items-start justify-between gap-4 border-t border-border px-4 py-3">
          <div>
            <label id="slack-guest-access-label" className="block text-sm font-medium text-foreground">
              {SLACK_GUEST_ACCESS.title}
            </label>
            <p className="mt-0.5 text-xs text-muted-foreground">{SLACK_GUEST_ACCESS.description}</p>
          </div>
          <Switch
            id="slack-guest-access-toggle"
            checked={guestAccessEnabled}
            disabled={guestAccessDisabled}
            onCheckedChange={onGuestAccessChange}
            aria-labelledby="slack-guest-access-label"
          />
        </div>
      )}

      <div className="border-t border-border px-4 py-3">
        <p className="mb-2 text-sm font-medium text-foreground">Slash commands</p>
        <dl className="space-y-0.5">
          {SLACK_COMMANDS.map((command) => (
            <div key={command.cmd} className="flex items-baseline gap-2.5">
              <dt className="shrink-0">
                <code className="rounded bg-card px-1.5 py-0.5 font-mono text-xs text-foreground ring-1 ring-border ring-inset">
                  /lookout {command.cmd}
                </code>
              </dt>
              <dd className="text-xs text-muted-foreground">{command.desc}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
